import { colorBaseGreyWarm700, colorBaseWhite } from '@issuu/silkscreen';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { ReaderMessageType } from '../../constants';
import type { Message, MessageHandler, ReaderViewState } from '../../types';
import DocumentContext from '../DocumentPage/DocumentContext';
import type { GridLayout } from '../DocumentPage/useGridLayout';
import { ScreenReaderH1 } from '../ScreenReaderOnly';
import { getReaderSrc } from './getReaderSrc';
import ReaderIframe from './ReaderIframe';
import { useReaderEventHandler, useSendReaderMessage } from './useReaderMessaging';

const Root = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${colorBaseWhite};
    background-color: ${colorBaseGreyWarm700};
    grid-area: reader;
`;

const cookiebotConsent = () => {
    // XXX: This is super hacky.
    /* There are no TS declarations nor much documentation for Cookiebot, and
        this function seems to transfer consent from a nested reader or vice
        versa. Uncertain precisely what this does. -gks */
    const win = window as unknown as Record<string, unknown>;
    if (win.Cookiebot && win.CookiebotDialog) {
        const cookiebot = win.Cookiebot as {
            hasResponse: boolean;
            regulations?: { gdprApplies: boolean };
        };
        const cookiebotDialog = win.CookiebotDialog as {
            consentLevel?: string;
            submitConsent?: (consent: boolean, url: string) => void;
        };
        if (
            !cookiebot.hasResponse &&
            cookiebot.regulations?.gdprApplies &&
            cookiebotDialog.consentLevel === 'implied' &&
            cookiebotDialog.submitConsent
        ) {
            cookiebotDialog.submitConsent(true, document.location.href);
        }
    }
};

const useKeyControls = (sendMessage: (msg: ReaderMessageType) => void): void => {
    useEffect(() => {
        const listener = (e: KeyboardEvent) => {
            switch (e.code) {
                case 'ArrowRight':
                    sendMessage(ReaderMessageType.keyRight);
                    return;
                case 'ArrowLeft':
                    sendMessage(ReaderMessageType.keyLeft);
                    return;
                case 'KeyF': {
                    if (e.ctrlKey || e.altKey) {
                        sendMessage(ReaderMessageType.showFindDialog);
                        e.preventDefault();
                    }
                    return;
                }
                default:
                    return;
            }
        };
        window.addEventListener('keydown', listener);
        return () => {
            window.removeEventListener('keydown', listener);
        };
    }, [sendMessage]);
};

type PageMessageType = 'documentLoaded' | 'pageChanged';
type BaseMessageType = Exclude<Message['type'], PageMessageType>;
type MessageTypeHandlerIndex = {
    [T in BaseMessageType]: () => void;
} & {
    [T in PageMessageType]: (data: Readonly<number[]>) => void;
};
let messageHandlers: MessageTypeHandlerIndex;

const singleColumnLayouts: GridLayout[] = ['tiny', 'small', 'singleColumn'];

export const EmbedReader: React.FC = () => {
    const {
        document: documentData,
        hosts,
        // For SSR to work properly, referrer cannot be drawn from window (which
        // does not exist in SSR context).
        // Referrer is instead gotten from the request object in the handler, and
        // passed on via prop/context.
        referrer: referrerUrl,
        publisher: {
            username,
            licenses: { defaultLargeViewReader },
        },
        appState: {
            sendMessageToReader,
            readerRef,
            currentLayout: { name: layout },
            currentPageNumbers: [currentPageNumbers, setCurrentPageNumbers],
            readerViewState: [viewState, setViewState],
        },
        trackers: { googleAnalytics },
    } = useContext(DocumentContext);

    const lockWideScreen = singleColumnLayouts.includes(layout);

    const readerSrc = useMemo(
        () =>
            getReaderSrc({
                hosts,
                username,
                documentName: documentData.documentName,
                initialPageNumber: currentPageNumbers[0],
                referrerUrl,
                delayTracking: false,
            }),
        [],
    );
    const readerUrl = new URL(readerSrc, `https://${hosts.main}`);
    const [setReaderFrame, sendMessage] = useSendReaderMessage();
    sendMessageToReader.current = sendMessage;

    const [previousViewState, setPreviousViewState] = useState<
        Exclude<ReaderViewState, 'fullscreen'>
    >(viewState === 'fullscreen' ? (defaultLargeViewReader ? 'large' : 'small') : viewState);

    const gaPageviewTimeoutRef = useRef<number>(0);
    // Initial pageview is handled in "DocumentPage". In this component, it
    // only handle the extra pageviews, triggered by flipping pages
    let previousTrackedPageNumbers = currentPageNumbers;
    const pageUpdater = useCallback((data: Readonly<number[]>) => {
        // Clear the previous timeout, whenever page changed
        window.clearTimeout(gaPageviewTimeoutRef.current);
        // Setup new timeout, when none of the page numbers in "data" has been tracked, in the previous pageviews
        if (
            previousTrackedPageNumbers.find(pageNumber => data.includes(pageNumber)) === undefined
        ) {
            gaPageviewTimeoutRef.current = window.setTimeout(() => {
                // Track GA pageviews, if the pages haven't changed in 1 second
                data.forEach(pageNumber =>
                    googleAnalytics('send', {
                        hitType: 'pageview',
                        page: `/${username}/docs/${documentData.documentName}/${pageNumber}`,
                    }),
                );
                // Remember the tracked page numbers
                previousTrackedPageNumbers = [...data];
            }, 1000);
        }
        // fix 'readonly' and cannot be assigned to the mutable type'
        setCurrentPageNumbers(data.slice(0));
    }, []);

    messageHandlers = {
        reader3Interacted: cookiebotConsent,
        documentLoaded: pageUpdater,
        pageChanged: pageUpdater,
        reader3LargeViewClick: useCallback(() => {
            const newViewState = viewState === 'small' ? 'large' : 'small';
            setViewState(newViewState);
            sendMessage(
                newViewState === 'small'
                    ? ReaderMessageType.showLargeViewButtonDisengaged
                    : ReaderMessageType.showLargeViewButtonEngaged,
            );
        }, [viewState, sendMessage, setViewState]),
        reader3EnterFullScreen: useCallback(() => {
            if (viewState !== 'fullscreen') {
                setPreviousViewState(viewState);
                setViewState('fullscreen');
            }
        }, [viewState, setViewState, setPreviousViewState]),
        reader3ExitFullScreen: useCallback(() => {
            setViewState(previousViewState);
        }, [previousViewState, setViewState]),
        reader3Available: useCallback(() => {
            sendMessage(
                defaultLargeViewReader || lockWideScreen
                    ? ReaderMessageType.hideLargeViewButton
                    : viewState === 'small'
                    ? ReaderMessageType.showLargeViewButtonDisengaged
                    : ReaderMessageType.showLargeViewButtonEngaged,
            );
        }, [viewState, lockWideScreen, sendMessage]),
    };

    useEffect(() => {
        if (defaultLargeViewReader) return;
        if (lockWideScreen) {
            sendMessage(ReaderMessageType.hideLargeViewButton);
        } else {
            sendMessage(
                viewState === 'small'
                    ? ReaderMessageType.showLargeViewButtonDisengaged
                    : ReaderMessageType.showLargeViewButtonEngaged,
            );
        }
    }, [sendMessage, viewState, lockWideScreen]);

    const readerMessageHandler = useCallback<MessageHandler>(msg => {
        if (typeof messageHandlers[msg.type] !== 'function') {
            // Just in case, we receive a message we can't handle
            return;
        }
        if ('data' in msg) {
            messageHandlers[msg.type](msg.data);
        } else {
            messageHandlers[msg.type]();
        }
    }, []);
    useReaderEventHandler(readerUrl.origin, sendMessage, readerMessageHandler);
    useKeyControls(sendMessage);

    // This lazy loads the reader iframe and improves on main thread blocking time for mobile users
    const [readerRootRef, readerInView] = useInView({ triggerOnce: true });

    return (
        <Root
            ref={r => {
                // Update context
                readerRef.current = r;
                // Update visibility hook
                readerRootRef(r);
            }}
        >
            {layout === 'tiny' ? null : <ScreenReaderH1>{documentData.title}</ScreenReaderH1>}
            {readerInView ? (
                <ReaderIframe
                    readerUrl={readerSrc}
                    setReaderFrame={setReaderFrame}
                    title={documentData.title}
                />
            ) : null}
        </Root>
    );
};
